import { useEffect } from 'react';
import { useFlags as LDUseFlags, useLDClient } from 'launchdarkly-react-client-sdk';

import { useAuthQuery } from '../../auth/hooks/useAuthQuery';

interface Flags {
  billingDocumentsEnabled: boolean;
  enableApprovalFlowForSharedRider: boolean;
  enableDailyQueueAssignedLocations: boolean;
  enableDailyQueueBilledCancels: boolean;
  enableZendeskChat: boolean;
  enableSisImports: boolean;
  ssoEnabled: boolean;
  redesignedLoginPageEnabled: boolean;
  enableRidersAdvancedSorting: boolean;
  enableRidesAdvancedSorting: boolean;
  iepStructuredNotesEnabled: boolean;
  fullStoryEnabled: boolean;
  enableReportsServicesInvoiceGroups: boolean;
  rideiqRiderSummaryReportEnabled: boolean;
  jobTitleDropdownEnabled: boolean;
  enableBulkRiderStatusChange: boolean;
  rideiqMbmAuthorizedGroupsRedesignEnabled: boolean;
  sharedBillingRolesFilterEnabled: boolean;
}
export const LaunchDarklyProvider = ({ children }) => {
  const { user } = useAuthQuery();
  const ldClient = useLDClient();

  useEffect(() => {
    if (user && ldClient) {
      const env = (window.env.RIDEIQ_SUBENVIRONMENT || window.env.RIDEIQ_ENVIRONMENT).toLowerCase();

      ldClient.identify({
        kind: 'multi',
        organization: { key: 'rideiq', env: env },
        user: { key: user.slug, account_slug: user?.accountSlug },
        account: {
          key: user?.accountSlug,
          metro: user?.metroAreaName,
          region: user?.regionName,
        },
      });
    }
  }, [user, ldClient]);

  return children;
};

const useFlags = (): Flags => {
  const flags = LDUseFlags() ?? {};

  return {
    billingDocumentsEnabled: flags.sharedBillingDocumentsEnabled ?? false,
    enableApprovalFlowForSharedRider: flags.railsApprovalFlowForSharedRider || false,
    enableDailyQueueAssignedLocations: flags.rideiqDailyQueueAssignedLocations || false,
    enableDailyQueueBilledCancels: flags.rideiqDailyQueueBilledCancels || false,
    enableZendeskChat: flags.rideiqEnableZendeskChat || false,
    enableSisImports: flags.railsSisImportsEnabled || false,
    ssoEnabled: flags.rideiqSsoEnabled || false,
    redesignedLoginPageEnabled: flags.rideiqRedesignedLoginPageEnabled ?? false,
    enableRidersAdvancedSorting: flags.rideiqEnableRidersAdvancedSorting || false,
    enableRidesAdvancedSorting: flags.rideiqEnableRidesAdvancedSorting || false,
    iepStructuredNotesEnabled: flags.sharedIepStructuredNotesEnabled || false,
    fullStoryEnabled: flags.rideiqFullstory || false,
    enableReportsServicesInvoiceGroups: flags.railsReportsServicesInvoiceGroupsEnabled || false,
    rideiqRiderSummaryReportEnabled: flags.rideiqRiderSummaryReportEnabled || false,
    jobTitleDropdownEnabled: flags.sharedJobTitleDropdownEnabled || false,
    enableBulkRiderStatusChange: flags.rideiqBulkRiderStatusChangeEnabled || false,
    rideiqMbmAuthorizedGroupsRedesignEnabled: flags.rideiqMbmAuthorizedGroupsRedesignEnabled || false,
    sharedBillingRolesFilterEnabled: flags.sharedSharedBillingRolesFilterEnabled || false,
  };
};

export { useFlags };
